
import React from "react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import CountryFlag from "@/components/country/CountryFlag";

interface CountrySelectorProps {
  form: UseFormReturn<any>;
  countries: any[];
  onCountryChange: (value: string) => void;
  sectionIndex: number;
  isDisabled?: boolean;
  excludeCountries?: string[];
  label?: string;
  defaultCountry?: string;
}

const CountrySelector = ({
  form,
  countries,
  onCountryChange,
  sectionIndex,
  isDisabled = false,
  excludeCountries = [],
  label = "Document Country",
  defaultCountry,
}: CountrySelectorProps) => {
  // Auto-set default country if not yet selected
  React.useEffect(() => {
    const current = form.getValues(`sections.${sectionIndex}.country_code`);
    if ((!current || current === "") && defaultCountry) {
      form.setValue(`sections.${sectionIndex}.country_code`, defaultCountry);
      onCountryChange(defaultCountry);
    }
  }, [defaultCountry, sectionIndex, form, onCountryChange]);

  // Filter countries based on excludeCountries prop
  const filteredCountries = React.useMemo(() => {
    if (!excludeCountries || excludeCountries.length === 0) {
      return countries;
    }
    return countries.filter(
      (country) => !excludeCountries.includes(country.code)
    );
  }, [countries, excludeCountries]);

  return (
    <FormField
      control={form.control}
      name={`sections.${sectionIndex}.country_code`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            onValueChange={(value) => {
              onCountryChange(value);
              field.onChange(value);
              form.setValue(`sections.${sectionIndex}.document_type_id`, "");
            }}
            value={field.value}
            disabled={isDisabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <div className="flex items-center gap-2">
                      <CountryFlag
                        countryCode={country.code}
                        className="h-4 w-5"
                      />
                      <span>{country.name}</span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="loading-countries-placeholder" disabled>
                  Loading countries...
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default CountrySelector;
